import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Edit, Trash2, Users, Info, ChevronUp, ChevronDown, RefreshCw, HelpCircle, Lock, UserRound, Filter, Calendar, BookOpen, Layers, Copy, RotateCcw, X, Check } from 'lucide-react';
import { API } from '@/lib/helpers';

const BOOLEAN_FIELDS = [
  { key: 'apenas_auxiliares', label: 'Auxiliar de Jovens' },
  { key: 'batizado', label: 'Batizado' },
  { key: 'selado', label: 'Selado' },
  { key: 'grupo_visita', label: 'Grupo de Visita' },
  { key: 'organista', label: 'Organista' },
  { key: 'instrutora_musicalizacao', label: 'Instrutora de Musicalização' },
  { key: 'integrante_ebi', label: 'Integrante EBI' },
  { key: 'instrutor_gem', label: 'Instrutor GEM' },
  { key: 'aluno_gem', label: 'Aluno GEM' },
  { key: 'musico', label: 'Músico' },
];

const DEFAULT_ELIGIBILITY = {
  gender: 'todos',
  atribuicoes: [],
  filtros_pessoa: [],
  idade_min: 0,
  idade_max: 99
};

function AgeRangeSlider({ min, max, onChange }) {
  const [localMin, setLocalMin] = useState(min);
  const [localMax, setLocalMax] = useState(max);

  useEffect(() => { setLocalMin(min); setLocalMax(max); }, [min, max]);

  const handleMinChange = (e) => {
    const v = Math.min(Number(e.target.value), localMax - 1);
    setLocalMin(v);
    onChange(v, localMax);
  };
  const handleMaxChange = (e) => {
    const v = Math.max(Number(e.target.value), localMin + 1);
    setLocalMax(v);
    onChange(localMin, v);
  };

  const minPct = (localMin / 99) * 100;
  const maxPct = (localMax / 99) * 100;

  return (
    <div data-testid="age-range-slider">
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs">Faixa de idade</Label>
        <span className="text-xs font-medium text-slate-600">
          {localMin === 0 && localMax === 99 ? 'Todas as idades' : `${localMin} - ${localMax} anos`}
        </span>
      </div>
      <div className="relative h-8 flex items-center">
        <div className="absolute w-full h-1.5 bg-slate-200 rounded-full" />
        <div className="absolute h-1.5 bg-blue-400 rounded-full" style={{ left: `${minPct}%`, right: `${100 - maxPct}%` }} />
        <input type="range" min="0" max="99" value={localMin} onChange={handleMinChange}
          className="absolute w-full h-1.5 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-500 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow [&::-moz-range-thumb]:cursor-pointer"
          style={{ zIndex: localMin > 90 ? 5 : 3 }} data-testid="age-min-slider" />
        <input type="range" min="0" max="99" value={localMax} onChange={handleMaxChange}
          className="absolute w-full h-1.5 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-blue-500 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow [&::-moz-range-thumb]:cursor-pointer"
          style={{ zIndex: 4 }} data-testid="age-max-slider" />
      </div>
      <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
        <span>0</span><span>25</span><span>50</span><span>75</span><span>99</span>
      </div>
    </div>
  );
}

function FuncRow({ func, idx, total, handleMove, functions, eligiblePeople, rotationMap, setRestricDialog, openEdit, handleDuplicate, handleDelete }) {
  const knownAssignedPeople = (func.assigned_people || []).filter(pid => eligiblePeople.some(p => p.pessoa_id === pid && p.auxiliar_jovens));
  const validAssignedPeople = func.assigned_people || [];
  const removedAssignedCount = (func.assigned_people || []).length - validAssignedPeople.length;
  return (
    <div className={`px-4 py-3 hover:bg-slate-50 transition-colors ${!func.active ? 'opacity-50' : ''}`} data-testid={`sf-${func.function_id}`}>
      <div className="flex items-center gap-3">
        <div className="flex flex-col gap-0.5">
          <button onClick={() => handleMove(func, 'up')} disabled={idx === 0} className="text-slate-300 hover:text-slate-500 disabled:opacity-30" data-testid={`sf-up-${func.function_id}`}><ChevronUp className="w-3.5 h-3.5" /></button>
          <button onClick={() => handleMove(func, 'down')} disabled={idx === total - 1} className="text-slate-300 hover:text-slate-500 disabled:opacity-30" data-testid={`sf-down-${func.function_id}`}><ChevronDown className="w-3.5 h-3.5" /></button>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <p className="font-medium text-sm text-slate-900">{func.nome}</p>
            {!func.active && <Badge variant="outline" className="text-[10px] text-slate-400">Inativa</Badge>}
            <TooltipProvider delayDuration={200}>
              {func.qty_male > 0 && (
                <Tooltip><TooltipTrigger asChild><span className="inline-flex items-center justify-center w-5 h-5 rounded bg-blue-50 text-blue-600 text-[10px] font-bold">{func.qty_male}</span></TooltipTrigger><TooltipContent><p className="text-xs">{func.qty_male} menino{func.qty_male > 1 ? 's' : ''}</p></TooltipContent></Tooltip>
              )}
              {func.qty_female > 0 && (
                <Tooltip><TooltipTrigger asChild><span className="inline-flex items-center justify-center w-5 h-5 rounded bg-pink-50 text-pink-600 text-[10px] font-bold">{func.qty_female}</span></TooltipTrigger><TooltipContent><p className="text-xs">{func.qty_female} menina{func.qty_female > 1 ? 's' : ''}</p></TooltipContent></Tooltip>
              )}
              {func.eligibility?.gender !== 'todos' && (
                <Tooltip><TooltipTrigger asChild><span className="inline-flex items-center justify-center w-5 h-5 rounded bg-amber-50"><UserRound className={`w-3 h-3 ${func.eligibility.gender === 'masculino' ? 'text-blue-500' : 'text-pink-500'}`} /></span></TooltipTrigger><TooltipContent><p className="text-xs">Só {func.eligibility.gender === 'masculino' ? 'meninos' : 'meninas'}</p></TooltipContent></Tooltip>
              )}
              {(func.eligibility?.filtros_pessoa?.length > 0 || func.eligibility?.apenas_auxiliares !== false) && (
                <Tooltip><TooltipTrigger asChild><span className="inline-flex items-center justify-center w-5 h-5 rounded bg-emerald-50"><Filter className="w-3 h-3 text-emerald-600" /></span></TooltipTrigger><TooltipContent><p className="text-xs">{func.eligibility?.filtros_pessoa?.length || 1} filtro{(func.eligibility?.filtros_pessoa?.length || 1) > 1 ? 's' : ''} de pessoa</p></TooltipContent></Tooltip>
              )}
              {(func.eligibility?.idade_min > 0 || (func.eligibility?.idade_max && func.eligibility.idade_max < 99)) && (
                <Tooltip><TooltipTrigger asChild><span className="inline-flex items-center justify-center w-5 h-5 rounded bg-orange-50"><Calendar className="w-3 h-3 text-orange-500" /></span></TooltipTrigger><TooltipContent><p className="text-xs">Idade: {func.eligibility.idade_min} - {func.eligibility.idade_max} anos</p></TooltipContent></Tooltip>
              )}
              {rotationMap[func.function_id] && (
                <Tooltip><TooltipTrigger asChild><span className="inline-flex items-center justify-center w-5 h-5 rounded bg-cyan-50"><RefreshCw className="w-3 h-3 text-cyan-600" /></span></TooltipTrigger><TooltipContent><p className="text-xs">Revezamento: Lado {rotationMap[func.function_id].side} ({rotationMap[func.function_id].group_name})</p></TooltipContent></Tooltip>
              )}
              {func.restricoes?.length > 0 && (() => {
                const validRestrictions = (func.restricoes || []).filter(rid => functions.some(f => f.function_id === rid));
                if (validRestrictions.length === 0) return null;
                const sameGenderFuncs = functions.filter(f => f.function_id !== func.function_id && (func.eligibility?.gender === 'todos' || f.eligibility?.gender === func.eligibility?.gender || f.eligibility?.gender === 'todos'));
                const isExclusive = sameGenderFuncs.length > 0 && sameGenderFuncs.every(f => validRestrictions.includes(f.function_id));
                const names = validRestrictions.map(rid => functions.find(f => f.function_id === rid)?.nome).filter(Boolean);
                return <Tooltip><TooltipTrigger asChild><span className="inline-flex items-center justify-center w-5 h-5 rounded bg-amber-50"><Lock className="w-3 h-3 text-amber-600" /></span></TooltipTrigger><TooltipContent className="max-w-xs"><p className="text-xs">{isExclusive ? 'Exclusiva total' : `${validRestrictions.length} restrição(ões): ${names.join(', ')}`}</p></TooltipContent></Tooltip>;
              })()}
            </TooltipProvider>
          </div>
          {func.tipo === 'recitativo' && validAssignedPeople.length > 0 && (
            <p className="text-[11px] text-blue-500 mt-0.5">{validAssignedPeople.map(pid => {
              const p = eligiblePeople.find(x => x.pessoa_id === pid);
              return p ? (p.apelido || (p.nome_completo || '').split(' ')[0]) : 'Auxiliar';
            }).join(', ')}</p>
          )}
          {func.tipo === 'recitativo' && knownAssignedPeople.length < validAssignedPeople.length && (
            <p className="text-[11px] text-orange-500 mt-0.5" data-testid={`sf-removed-assigned-${func.function_id}`}>{validAssignedPeople.length - knownAssignedPeople.length} auxiliar(es) não exibido(s) na lista atual</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {(() => {
            const hasValid = (func.restricoes || []).some(rid => functions.some(f => f.function_id === rid));
            return <Button variant="ghost" size="icon" className={`h-8 w-8 ${hasValid ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 ring-1 ring-amber-300' : 'text-amber-600 hover:bg-amber-50'}`} onClick={() => setRestricDialog(func)} data-testid={`sf-restric-${func.function_id}`} title="Restrições"><Lock className="w-4 h-4" /></Button>;
          })()}
          <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50" onClick={() => openEdit(func)} data-testid={`sf-edit-${func.function_id}`}><Edit className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:bg-slate-100" onClick={() => handleDuplicate(func)} data-testid={`sf-dup-${func.function_id}`} title="Duplicar"><Copy className="w-4 h-4" /></Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => handleDelete(func.function_id, func.nome)} data-testid={`sf-delete-${func.function_id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
}


export default function EscalasSettingsTab({ filterType = 'all' }) {
  const [functions, setFunctions] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nome: '', qty_male: 0, qty_female: 0, eligibility: { ...DEFAULT_ELIGIBILITY } });
  const [saving, setSaving] = useState(false);

  // Rotation groups state
  const [rotationGroups, setRotationGroups] = useState([]);
  const [rotDialog, setRotDialog] = useState(false);
  const [rotEditing, setRotEditing] = useState(null);
  const [rotForm, setRotForm] = useState({ nome: '', lado_a: [], lado_b: [] });
  const [rotSaving, setRotSaving] = useState(false);
  const [showFuncInfo, setShowFuncInfo] = useState(false);
  const [showRotInfo, setShowRotInfo] = useState(false);
  const [restricDialog, setRestricDialog] = useState(null); // function object or null
  const [eligiblePeople, setEligiblePeople] = useState([]);
  const [rotateDialog, setRotateDialog] = useState(false);
  const [rotateSuggestions, setRotateSuggestions] = useState([]);
  const [rotateLoading, setRotateLoading] = useState(false);
  const [rotateGenderFilter, setRotateGenderFilter] = useState('masculino');
  const [cycleConfig, setCycleConfig] = useState({ cycle_duration_months: null, last_rotated_at: null });
  const [cycleDraftMonths, setCycleDraftMonths] = useState('');
  const [confirmEarlyApply, setConfirmEarlyApply] = useState(false);

  const fetchFunctions = () => {
    fetch(`${API}/service-functions`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setFunctions)
      .catch(() => {});
  };

  const fetchRotationGroups = () => {
    fetch(`${API}/service-rotations`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setRotationGroups)
      .catch(() => {});
  };

  useEffect(() => { fetchFunctions(); fetchRotationGroups(); fetchEligiblePeople(); fetchCycleConfig(); }, []);

  const fetchEligiblePeople = () => {
    fetch(`${API}/pessoas?status=aprovado`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setEligiblePeople)
      .catch(() => {});
  };

  const fetchCycleConfig = () => {
    fetch(`${API}/recitative-cycles/config`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { cycle_duration_months: null, last_rotated_at: null })
      .then(data => {
        setCycleConfig(data);
        setCycleDraftMonths(data.cycle_duration_months || '');
      })
      .catch(() => {});
  };

  const saveCycleConfig = async (months) => {
    try {
      await fetch(`${API}/recitative-cycles/config`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ cycle_duration_months: months || null })
      });
      setCycleConfig(prev => ({ ...prev, cycle_duration_months: months || null }));
      toast.success('Ciclo atualizado!');
    } catch { toast.error('Erro ao salvar ciclo'); }
  };

  const resetForm = () => {
    setForm({ nome: '', tipo: '', qty_male: 0, qty_female: 0, restricoes: [], assigned_people: [], rotate_leadership: false, eligibility: { ...DEFAULT_ELIGIBILITY } });
    setEditing(null);
  };

  const handleSave = async () => {
    const normalizedForm = form.tipo === 'recitativo'
      ? { ...form, eligibility: { ...form.eligibility, filtros_pessoa: ['apenas_auxiliares'] } }
      : form;
    if (!form.tipo) { toast.error('Selecione o tipo de função'); return; }
    if (!form.nome.trim()) { toast.error('Nome da função é obrigatório'); return; }
    if (form.qty_male <= 0 && form.qty_female <= 0) { toast.error('Informe ao menos 1 menino ou 1 menina'); return; }
    if (form.tipo !== 'recitativo' && !form.eligibility.filtros_pessoa?.length) { toast.error('Selecione ao menos um filtro de pessoa'); return; }
    setSaving(true);
    try {
      const url = editing ? `${API}/service-functions/${editing.function_id}` : `${API}/service-functions`;
      const method = editing ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(normalizedForm) });
      if (!r.ok) { const err = await r.json(); throw new Error(err.detail); }
      toast.success(editing ? 'Função atualizada!' : 'Função criada!');
      setDialog(false);
      resetForm();
      fetchFunctions();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const handleDelete = async (id, nome) => {
    if (!window.confirm(`Excluir "${nome}"?`)) return;
    try {
      await fetch(`${API}/service-functions/${id}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Função excluída');
      fetchFunctions();
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleDuplicate = async (func) => {
    try {
      const body = {
        nome: `${func.nome} (cópia)`,
        tipo: func.tipo || 'outros',
        qty_male: func.qty_male,
        qty_female: func.qty_female,
        eligibility: func.eligibility,
        restricoes: func.restricoes || []
      };
      const r = await fetch(`${API}/service-functions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body) });
      if (!r.ok) { const err = await r.json(); throw new Error(err.detail); }
      toast.success('Função duplicada!');
      fetchFunctions();
    } catch (err) { toast.error(err.message); }
  };

  const handleSuggestRotation = async () => {
    setRotateLoading(true);
    try {
      const r = await fetch(`${API}/recitative-cycles/suggest`, { method: 'POST', credentials: 'include' });
      if (!r.ok) { const err = await r.json(); throw new Error(err.detail); }
      const data = await r.json();
      setRotateSuggestions(data.suggestions || []);
      setRotateDialog(true);
    } catch (err) { toast.error(err.message); }
    setRotateLoading(false);
  };

  const isCycleComplete = () => {
    const { cycle_duration_months, last_rotated_at } = cycleConfig;
    if (!cycle_duration_months || !last_rotated_at) return true;
    const diffDays = Math.floor((new Date() - new Date(last_rotated_at)) / (1000 * 60 * 60 * 24));
    return diffDays >= cycle_duration_months * 30;
  };

  const handleApplyClick = () => {
    if (!isCycleComplete()) {
      setConfirmEarlyApply(true);
    } else {
      handleApplyRotation();
    }
  };

  const handleApplyRotation = async () => {
    setConfirmEarlyApply(false);
    setRotateLoading(true);
    try {
      // Only apply for the currently selected gender filter
      const assignments = rotateSuggestions
        .filter(s => {
          const func = functions.find(f => f.function_id === s.function_id);
          const gender = func?.eligibility?.gender || 'todos';
          return gender === 'todos' || gender === rotateGenderFilter;
        })
        .map(s => ({
          function_id: s.function_id,
          people: s.suggested_people.map(p => p.pessoa_id)
        }));
      const r = await fetch(`${API}/recitative-cycles/apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ assignments })
      });
      if (!r.ok) { const err = await r.json(); throw new Error(err.detail); }
      toast.success(`Rotação de ${rotateGenderFilter === 'masculino' ? 'meninos' : 'meninas'} aplicada!`);
      setRotateDialog(false);
      fetchFunctions();
      fetchCycleConfig();
    } catch (err) { toast.error(err.message); }
    setRotateLoading(false);
  };

  const handleToggleActive = async (func) => {
    try {
      await fetch(`${API}/service-functions/${func.function_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ active: !func.active })
      });
      fetchFunctions();
    } catch { toast.error('Erro ao alterar status'); }
  };

  const handleMove = async (func, direction) => {
    const idx = functions.findIndex(f => f.function_id === func.function_id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= functions.length) return;
    const other = functions[swapIdx];
    try {
      await Promise.all([
        fetch(`${API}/service-functions/${func.function_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ order: other.order }) }),
        fetch(`${API}/service-functions/${other.function_id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ order: func.order }) })
      ]);
      fetchFunctions();
    } catch { toast.error('Erro ao reordenar'); }
  };

  const toggleFiltroPessoa = (key) => {
    const current = form.eligibility.filtros_pessoa || [];
    const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
    setForm({ ...form, eligibility: { ...form.eligibility, filtros_pessoa: next } });
  };

  const openEdit = (func) => {
    setEditing(func);
    const elig = func.eligibility || {};
    const activeAssignedPeople = func.assigned_people || [];
    setForm({
      nome: func.nome,
      tipo: func.tipo || 'outros',
      qty_male: func.qty_male,
      qty_female: func.qty_female,
      active: func.active !== false,
      restricoes: func.restricoes || [],
      assigned_people: activeAssignedPeople,
      rotate_leadership: func.rotate_leadership || false,
      eligibility: {
        gender: elig.gender || 'todos',
        atribuicoes: [],
        filtros_pessoa: elig.filtros_pessoa || (elig.apenas_auxiliares !== false ? ['apenas_auxiliares'] : []),
        idade_min: elig.idade_min ?? 0,
        idade_max: elig.idade_max ?? 99
      }
    });
    setDialog(true);
  };

  // Build rotation info map: function_id -> {group_name, side}
  const rotationMap = {};
  rotationGroups.forEach(g => {
    (g.lado_a || []).forEach(fid => { rotationMap[fid] = { group_name: g.nome, side: 'A' }; });
    (g.lado_b || []).forEach(fid => { rotationMap[fid] = { group_name: g.nome, side: 'B' }; });
  });

  const resetRotForm = () => { setRotForm({ nome: '', lado_a: [], lado_b: [] }); setRotEditing(null); };

  const handleRotSave = async () => {
    if (!rotForm.nome.trim()) { toast.error('Nome do revezamento é obrigatório'); return; }
    if (!rotForm.lado_a.length || !rotForm.lado_b.length) { toast.error('Ambos os lados devem ter ao menos uma função'); return; }
    setRotSaving(true);
    try {
      const url = rotEditing ? `${API}/service-rotations/${rotEditing.rotation_group_id}` : `${API}/service-rotations`;
      const method = rotEditing ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(rotForm) });
      if (!r.ok) { const err = await r.json(); throw new Error(err.detail); }
      toast.success(rotEditing ? 'Revezamento atualizado!' : 'Revezamento criado!');
      setRotDialog(false);
      resetRotForm();
      fetchRotationGroups();
    } catch (err) { toast.error(err.message); } finally { setRotSaving(false); }
  };

  const handleRotDelete = async (id, nome) => {
    if (!window.confirm(`Excluir revezamento "${nome}"?`)) return;
    try {
      await fetch(`${API}/service-rotations/${id}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Revezamento excluído');
      fetchRotationGroups();
    } catch { toast.error('Erro ao excluir'); }
  };

  const openRotEdit = (group) => {
    setRotEditing(group);
    setRotForm({ nome: group.nome, lado_a: group.lado_a || [], lado_b: group.lado_b || [] });
    setRotDialog(true);
  };

  const toggleRotFunction = (side, fid) => {
    const current = rotForm[side] || [];
    const otherSide = side === 'lado_a' ? 'lado_b' : 'lado_a';
    if (current.includes(fid)) {
      setRotForm({ ...rotForm, [side]: current.filter(id => id !== fid) });
    } else {
      setRotForm({
        ...rotForm,
        [side]: [...current, fid],
        [otherSide]: (rotForm[otherSide] || []).filter(id => id !== fid)
      });
    }
  };

  const getAvailableFunctionsForRotation = () => {
    const usedFids = new Set();
    rotationGroups.forEach(g => {
      if (rotEditing && g.rotation_group_id === rotEditing.rotation_group_id) return;
      (g.lado_a || []).forEach(fid => usedFids.add(fid));
      (g.lado_b || []).forEach(fid => usedFids.add(fid));
    });
    return functions.filter(f => !usedFids.has(f.function_id) || rotForm.lado_a.includes(f.function_id) || rotForm.lado_b.includes(f.function_id));
  };

  const filteredFunctions = filterType === 'recitativo' ? functions.filter(f => (f.tipo || 'outros') === 'recitativo')
    : filterType === 'outros' ? functions.filter(f => (f.tipo || 'outros') === 'outros')
    : functions;

  const sectionTitle = filterType === 'recitativo' ? 'Recitativos' : filterType === 'outros' ? 'Outros Serviços' : filterType === 'revezamentos' ? 'Revezamentos' : 'Funções de Serviço';
  const sectionDesc = filterType === 'recitativo' ? 'Funções de recitativo com escala fixa e rotação periódica.'
    : filterType === 'outros' ? 'Funções de apoio e serviço nos eventos.'
    : filterType === 'revezamentos' ? 'Defina grupos de funções que devem alternar automaticamente.'
    : 'Configure as funções que serão usadas nas escalas dos eventos.';

  return (
    <div className="space-y-4">
      {filterType !== 'revezamentos' && (
      <>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-1.5" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {sectionTitle}
            <button onClick={() => setShowFuncInfo(true)} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="func-info-btn"><HelpCircle className="w-4 h-4" /></button>
          </h3>
          <p className="text-sm text-slate-500">{sectionDesc}</p>
        </div>
        <div className="flex items-center gap-2">
          {filterType === 'recitativo' && filteredFunctions.length > 0 && (
            <TooltipProvider><Tooltip><TooltipTrigger asChild>
              <Button size="sm" variant="outline" onClick={() => { setRotateSuggestions([]); setRotateDialog(true); handleSuggestRotation(); }} className="border-blue-300 text-blue-600 hover:bg-blue-50" data-testid="rotate-recitativos-btn">
                <RotateCcw className="w-4 h-4" />
              </Button>
            </TooltipTrigger><TooltipContent><p className="text-xs">Rotacionar recitativos</p></TooltipContent></Tooltip></TooltipProvider>
          )}
          <Button size="sm" onClick={() => { resetForm(); setForm(prev => ({ ...prev, tipo: filterType === 'recitativo' ? 'recitativo' : filterType === 'outros' ? 'outros' : '', eligibility: { ...prev.eligibility, filtros_pessoa: filterType === 'recitativo' ? ['apenas_auxiliares'] : (prev.eligibility?.filtros_pessoa || []) } })); setDialog(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-service-function-btn">
            <Plus className="w-4 h-4 mr-1" />Nova Função
          </Button>
        </div>
      </div>

      {filteredFunctions.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Nenhuma função cadastrada</p>
          <p className="text-xs mt-1">{filterType === 'recitativo' ? 'Crie funções de recitativo para gerenciar escalas fixas.' : 'Crie funções como "Apoio Oração", "Acolhimento", etc.'}</p>
        </div>
      ) : (
        <div data-testid="service-functions-list">
          {(filterType === 'recitativo' || filterType === 'outros') ? (
            [{key: 'masculino', label: 'Meninos', color: 'blue'}, {key: 'feminino', label: 'Meninas', color: 'pink'}].map((group, gi) => {
              const gFuncs = filteredFunctions.filter(f => (f.eligibility?.gender || 'todos') === group.key);
              if (gFuncs.length === 0) return null;
              return (
                <div key={group.key} className={gi > 0 ? 'mt-4' : ''}>
                  <div className="rounded-lg border border-slate-200 overflow-hidden">
                    <p className={`text-[11px] font-semibold uppercase tracking-wider px-4 pt-3 pb-1 ${group.color === 'blue' ? 'text-blue-500' : 'text-pink-500'}`}>{group.label}</p>
                    <div className="divide-y divide-slate-100">
                    {gFuncs.map((func, idx) => (
                      <FuncRow key={func.function_id} func={func} idx={idx} total={gFuncs.length} handleMove={handleMove} functions={functions} eligiblePeople={eligiblePeople} rotationMap={rotationMap} setRestricDialog={setRestricDialog} openEdit={openEdit} handleDuplicate={handleDuplicate} handleDelete={handleDelete} />
                    ))}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="rounded-lg border border-slate-200 overflow-hidden">
              <div className="divide-y divide-slate-100">
              {filteredFunctions.map((func, idx) => (
                <FuncRow key={func.function_id} func={func} idx={idx} total={filteredFunctions.length} handleMove={handleMove} functions={functions} eligiblePeople={eligiblePeople} rotationMap={rotationMap} setRestricDialog={setRestricDialog} openEdit={openEdit} handleDuplicate={handleDuplicate} handleDelete={handleDelete} />
              ))}
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={(v) => { setDialog(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Editar Função' : 'Nova Função de Serviço'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {(filterType !== 'recitativo' && filterType !== 'outros') && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Label className="text-xs">Tipo de Função *</Label>
                <TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger><TooltipContent className="max-w-xs"><p className="text-xs">Recitativo: exclusivo para recitativos normais, em fila. Outros: engloba todas as demais funções (Oração de Súplica, Oração do Pai Nosso, Contador de Recitativos, Recitativos Individuais, etc.)</p></TooltipContent></Tooltip></TooltipProvider>
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="sf-form-tipo">
                {[{ value: 'recitativo', label: 'Recitativo', icon: BookOpen }, { value: 'outros', label: 'Outros', icon: Layers }].map(t => {
                  const Icon = t.icon;
                  const selected = form.tipo === t.value;
                  return (
                    <button key={t.value} type="button" onClick={() => {
                      const newForm = { ...form, tipo: t.value };
                      if (t.value === 'recitativo') {
                        newForm.eligibility = { ...newForm.eligibility, filtros_pessoa: ['apenas_auxiliares'] };
                      }
                      setForm(newForm);
                    }}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border-2 transition-colors ${selected ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                      data-testid={`sf-tipo-${t.value}`}>
                      <Icon className="w-4 h-4" />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>
            )}
            <div>
              <Label>Nome da Função *</Label>
              {(filterType === 'outros' || form.tipo === 'outros') ? (
                <select value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-testid="sf-form-nome">
                  <option value="">Selecione...</option>
                  {['Apoio na Oração', 'Oração do Pai Nosso', 'Oração de Súplica', 'Contador', 'Recitativo Individual'].map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : (
                <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex: 1ª Fila, 2ª Fila" data-testid="sf-form-nome" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Qtd. Meninos</Label>
                <Input type="number" min="0" value={form.qty_male} onChange={(e) => {
                  const v = parseInt(e.target.value) || 0;
                  const gender = v > 0 && form.qty_female > 0 ? 'todos' : v > 0 ? 'masculino' : form.qty_female > 0 ? 'feminino' : 'todos';
                  setForm({ ...form, qty_male: v, eligibility: { ...form.eligibility, gender } });
                }} data-testid="sf-form-qty-male" />
              </div>
              <div>
                <Label>Qtd. Meninas</Label>
                <Input type="number" min="0" value={form.qty_female} onChange={(e) => {
                  const v = parseInt(e.target.value) || 0;
                  const gender = form.qty_male > 0 && v > 0 ? 'todos' : form.qty_male > 0 ? 'masculino' : v > 0 ? 'feminino' : 'todos';
                  setForm({ ...form, qty_female: v, eligibility: { ...form.eligibility, gender } });
                }} data-testid="sf-form-qty-female" />
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg p-3 space-y-4">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium text-slate-700">Elegibilidade</p>
                <TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger><TooltipContent className="max-w-xs"><p className="text-xs">Defina quem pode ser escalado para esta função. Configure gênero, faixa de idade, atribuições e características adicionais.</p></TooltipContent></Tooltip></TooltipProvider>
              </div>

              {/* Gênero derivado automaticamente das quantidades */}
              <div>
                <Label className="text-xs mb-1 block">Gênero</Label>
                <span className="px-2.5 py-1 rounded-md text-xs font-medium border border-slate-200 bg-slate-50 text-slate-600">
                  {form.qty_male > 0 && form.qty_female > 0 ? 'Todos' : form.qty_male > 0 ? 'Só meninos' : form.qty_female > 0 ? 'Só meninas' : 'Defina as quantidades'}
                </span>
              </div>

              {/* Age range slider */}
              <AgeRangeSlider
                min={form.eligibility.idade_min ?? 0}
                max={form.eligibility.idade_max ?? 99}
                onChange={(min, max) => setForm({ ...form, eligibility: { ...form.eligibility, idade_min: min, idade_max: max } })}
              />

              {/* Person boolean filters as cards */}
              <div>
                <Label className="text-xs mb-1.5 block">Filtros de pessoa</Label>
                {form.tipo === 'recitativo' ? (
                  <span className="px-2 py-1 rounded-md text-xs font-medium border border-blue-500 bg-blue-50 text-blue-700">Auxiliar de Jovens</span>
                ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="sf-filtros-pessoa">
                  <button type="button" onClick={() => {
                    const allKeys = BOOLEAN_FIELDS.map(f => f.key);
                    const allSelected = allKeys.every(k => (form.eligibility.filtros_pessoa || []).includes(k));
                    setForm({ ...form, eligibility: { ...form.eligibility, filtros_pessoa: allSelected ? [] : allKeys } });
                  }} className={`px-2 py-1 rounded-md text-xs font-medium border transition-colors ${BOOLEAN_FIELDS.every(f => (form.eligibility.filtros_pessoa || []).includes(f.key)) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`} data-testid="sf-filtro-todos">
                    Todos
                  </button>
                  {BOOLEAN_FIELDS.map(f => {
                    const selected = (form.eligibility.filtros_pessoa || []).includes(f.key);
                    return (
                      <button key={f.key} type="button" onClick={() => toggleFiltroPessoa(f.key)}
                        className={`px-2 py-1 rounded-md text-xs font-medium border transition-colors ${selected ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                        data-testid={`sf-filtro-${f.key}`}>
                        {f.label}
                      </button>
                    );
                  })}
                </div>
                )}
              </div>

            </div>

            {/* Recitative: assigned people */}
            {form.tipo === 'recitativo' && (
              <div className="border border-slate-200 rounded-lg p-3 space-y-3 mt-4">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-medium text-slate-700">Escala fixa de recitativo</p>
                  <TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger><TooltipContent className="max-w-xs"><p className="text-xs">Defina as pessoas fixas para esta função de recitativo. Elas serão automaticamente incluídas nos eventos e só mudarão quando houver rotação.</p></TooltipContent></Tooltip></TooltipProvider>
                </div>
                <div>
                  <Label className="text-xs mb-1.5 block">Pessoas atribuídas ({(form.assigned_people || []).length}/{(form.qty_male || 0) + (form.qty_female || 0)})</Label>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {eligiblePeople
                      .filter(p => {
                        if (!p.auxiliar_jovens) return false;
                        const g = (p.genero || '').toLowerCase();
                        const gender = form.eligibility?.gender || 'todos';
                        if (gender === 'masculino' && g !== 'masculino') return false;
                        if (gender === 'feminino' && g !== 'feminino') return false;
                        return true;
                      })
                      .sort((a, b) => {
                        const aSelected = (form.assigned_people || []).includes(a.pessoa_id) ? 0 : 1;
                        const bSelected = (form.assigned_people || []).includes(b.pessoa_id) ? 0 : 1;
                        return aSelected - bSelected || (a.apelido || a.nome_completo || '').localeCompare(b.apelido || b.nome_completo || '');
                      })
                      .map(p => {
                        const selected = (form.assigned_people || []).includes(p.pessoa_id);
                        const nome = p.apelido || (p.nome_completo || '').split(' ')[0];
                        const isMale = (p.genero || '').toLowerCase() === 'masculino';
                        return (
                          <button key={p.pessoa_id} type="button" onClick={() => {
                            setForm(prev => {
                              const current = prev.assigned_people || [];
                              const isSelected = current.includes(p.pessoa_id);
                              const next = isSelected ? current.filter(id => id !== p.pessoa_id) : [...current, p.pessoa_id];
                              return { ...prev, assigned_people: next };
                            });
                          }}
                            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs text-left transition-colors ${selected ? 'bg-blue-100 border border-blue-300 text-blue-800' : 'border border-slate-200 text-slate-600 hover:border-slate-300'}`}
                            data-testid={`sf-assign-${p.pessoa_id}`}>
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isMale ? 'bg-blue-400' : 'bg-pink-400'}`} />
                            <span className="flex-1">{nome}</span>
                            {selected && <Check className="w-3 h-3 text-blue-600" />}
                          </button>
                        );
                      })}
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
                  <Switch checked={form.rotate_leadership || false} onCheckedChange={(v) => setForm({ ...form, rotate_leadership: v })} data-testid="sf-rotate-leadership-toggle" />
                  <Label className="text-xs text-slate-600">Alternar responsabilidade entre atribuídos</Label>
                  <TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger><TooltipContent className="max-w-xs"><p className="text-xs">A cada evento, o sistema alterna automaticamente qual dos atribuídos é o responsável por escrever os recitativos. Se o responsável marcar ausência, a responsabilidade é transferida ao substituto.</p></TooltipContent></Tooltip></TooltipProvider>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              {editing && (
                <div className="flex items-center gap-2">
                  <Switch checked={form.active !== false} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="sf-form-active-toggle" />
                  <Label className="text-sm text-slate-600">{form.active !== false ? 'Ativa' : 'Inativa'}</Label>
                </div>
              )}
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 ml-auto" data-testid="sf-save-btn">
                {saving ? 'Salvando...' : editing ? 'Atualizar' : 'Criar Função'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </>
      )}

      {/* ==================== ROTATION GROUPS SECTION ==================== */}
      {filterType === 'revezamentos' && (
      <>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-1.5" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {sectionTitle}
            <button onClick={() => setShowRotInfo(true)} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="rot-info-btn"><HelpCircle className="w-4 h-4" /></button>
          </h3>
          <p className="text-sm text-slate-500">{sectionDesc}</p>
        </div>
        <Button size="sm" onClick={() => { resetRotForm(); setRotDialog(true); }} className="bg-cyan-600 hover:bg-cyan-700" data-testid="add-rotation-group-btn">
          <Plus className="w-4 h-4 mr-1" />Novo Revezamento
        </Button>
      </div>

        {rotationGroups.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <RefreshCw className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Nenhum revezamento configurado</p>
            <p className="text-xs mt-1">Crie um grupo para alternar funções entre eventos automaticamente.</p>
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid="rotation-groups-list">
            {rotationGroups.map(g => {
              const getFunc = (fid) => functions.find(f => f.function_id === fid);
              const getFuncName = (fid) => getFunc(fid)?.nome || fid;
              const genderDotClass = (fid) => {
                const gen = getFunc(fid)?.eligibility?.gender;
                if (gen === 'masculino') return 'bg-blue-400';
                if (gen === 'feminino') return 'bg-pink-400';
                return 'bg-violet-400';
              };
              const renderFuncBadge = (fid, bgCls) => {
                const func = getFunc(fid);
                return (
                  <TooltipProvider key={fid}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={`inline-flex items-center gap-1 text-xs ${bgCls} px-1.5 py-0.5 rounded `}>
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${genderDotClass(fid)}`} />
                          {getFuncName(fid)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <FuncTooltipContent func={func} />
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              };
              return (
                <div key={g.rotation_group_id} className={`px-4 py-3 border-b last:border-b-0 border-slate-100 hover:bg-slate-50 transition-colors ${!g.ativo ? 'opacity-50' : ''}`} data-testid={`rg-${g.rotation_group_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm text-slate-900">{g.nome}</p>
                        {!g.ativo && <Badge variant="outline" className="text-[10px] text-slate-400">Inativo</Badge>}
                      </div>
                      <div className="flex flex-col gap-1 mt-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">A:</span>
                          {(g.lado_a || []).map(fid => renderFuncBadge(fid, 'bg-cyan-50 text-cyan-700'))}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">B:</span>
                          {(g.lado_b || []).map(fid => renderFuncBadge(fid, 'bg-teal-50 text-teal-700'))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-cyan-600 hover:bg-cyan-50" onClick={() => openRotEdit(g)} data-testid={`rg-edit-${g.rotation_group_id}`}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50" onClick={() => handleRotDelete(g.rotation_group_id, g.nome)} data-testid={`rg-delete-${g.rotation_group_id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

      {/* Rotation Group Dialog */}
      <Dialog open={rotDialog} onOpenChange={(v) => { setRotDialog(v); if (!v) resetRotForm(); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{rotEditing ? 'Editar Revezamento' : 'Novo Revezamento'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Revezamento *</Label>
              <Input value={rotForm.nome} onChange={(e) => setRotForm({ ...rotForm, nome: e.target.value })} placeholder="Ex: Orações, Recitativos" data-testid="rot-form-nome" />
            </div>
            <RotationSideSelector
              label="Lado A"
              side="lado_a"
              selected={rotForm.lado_a}
              availableFunctions={getAvailableFunctionsForRotation()}
              onToggle={(fid) => toggleRotFunction('lado_a', fid)}
              colorClass="cyan"
              filas={[]}
            />
            <RotationSideSelector
              label="Lado B"
              side="lado_b"
              selected={rotForm.lado_b}
              availableFunctions={getAvailableFunctionsForRotation()}
              onToggle={(fid) => toggleRotFunction('lado_b', fid)}
              colorClass="teal"
              filas={[]}
            />
            <Button onClick={handleRotSave} disabled={rotSaving} className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="rot-save-btn">
              {rotSaving ? 'Salvando...' : rotEditing ? 'Atualizar' : 'Criar Revezamento'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </>
      )}

      {/* Info Modal — Funções de Serviço */}
      <Dialog open={showFuncInfo} onOpenChange={setShowFuncInfo}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-base">Como funcionam as Funções de Serviço?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <div>
              <p className="font-semibold text-slate-800 mb-1">O que são funções de serviço?</p>
              <p>São tarefas que precisam ser realizadas em cada evento, como Apoio Oração, Recitativo Avulso, Contador, etc. Cada função define quantos meninos e meninas são necessários.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-800 mb-1">Elegibilidade</p>
              <p>Cada função possui critérios que determinam quem pode ser escalado: gênero, faixa de idade, filtros de pessoa (auxiliar, batizado, selado, etc.) e atribuições (filas).</p>
            </div>
            <div>
              <p className="font-semibold text-slate-800 mb-1">Sugestão automática</p>
              <p>Ao gerar sugestões para um evento, o sistema analisa os critérios de cada função e distribui as pessoas de forma justa, priorizando quem serviu menos recentemente.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-800 mb-1">Ordem e status</p>
              <p>Use as setas para definir a ordem de exibição. Funções desativadas não aparecem nas escalas, mas podem ser reativadas a qualquer momento.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Info Modal — Revezamentos */}
      <Dialog open={showRotInfo} onOpenChange={setShowRotInfo}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-base">Como funcionam os Revezamentos?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <div>
              <p className="font-semibold text-slate-800 mb-1">O que é um revezamento?</p>
              <p>É um mecanismo que alterna automaticamente funções entre eventos. Por exemplo: num evento, meninos fazem "Oração do Pai Nosso" e meninas fazem "Oração de Súplica"; no próximo evento, eles trocam.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-800 mb-1">Como funciona?</p>
              <p>Cada grupo de revezamento define dois lados (A e B), cada um com uma ou mais funções. A cada novo evento, o sistema verifica qual lado foi utilizado no último evento do mesmo tipo e ativa o lado oposto.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-800 mb-1">Exemplo prático</p>
              <p>Grupo "Orações" com Lado A = "Oração do Pai Nosso (masc)" e Lado B = "Oração do Pai Nosso (fem)". No Evento 1 aparece o Lado A; no Evento 2 aparece o Lado B; no Evento 3 volta ao Lado A, e assim por diante.</p>
            </div>
            <div>
              <p className="font-semibold text-slate-800 mb-1">Funções sem revezamento</p>
              <p>Funções que não pertencem a nenhum grupo de revezamento continuam aparecendo em todos os eventos normalmente, sem nenhuma alteração.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Restriction Modal */}
      <Dialog open={!!restricDialog} onOpenChange={(open) => { if (!open) setRestricDialog(null); }}>
        <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-600" />
              Restrições: {restricDialog?.nome}
            </DialogTitle>
            <DialogDescription>
              Quem for escalado nesta função NÃO poderá exercer as funções selecionadas no mesmo evento.
            </DialogDescription>
          </DialogHeader>
          {restricDialog && (() => {
            const funcGender = restricDialog.eligibility?.gender || 'todos';
            const sameGenderFuncs = functions.filter(f =>
              f.function_id !== restricDialog.function_id &&
              (funcGender === 'todos' || f.eligibility?.gender === funcGender || f.eligibility?.gender === 'todos')
            );
            const currentRestrictions = restricDialog.restricoes || [];
            const allSelected = sameGenderFuncs.length > 0 && sameGenderFuncs.every(f => currentRestrictions.includes(f.function_id));

            const toggleRestriction = async (fid) => {
              const newR = currentRestrictions.includes(fid)
                ? currentRestrictions.filter(id => id !== fid)
                : [...currentRestrictions, fid];
              const r = await fetch(`${API}/service-functions/${restricDialog.function_id}`, {
                method: 'PUT', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ restricoes: newR })
              });
              if (r.ok) {
                setRestricDialog({ ...restricDialog, restricoes: newR });
                setFunctions(prev => prev.map(f => f.function_id === restricDialog.function_id ? { ...f, restricoes: newR } : f));
              }
            };

            const toggleAll = async () => {
              const newR = allSelected ? [] : sameGenderFuncs.map(f => f.function_id);
              const r = await fetch(`${API}/service-functions/${restricDialog.function_id}`, {
                method: 'PUT', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ restricoes: newR })
              });
              if (r.ok) {
                setRestricDialog({ ...restricDialog, restricoes: newR });
                setFunctions(prev => prev.map(f => f.function_id === restricDialog.function_id ? { ...f, restricoes: newR } : f));
              }
            };

            return (
              <div className="space-y-3 pt-2" data-testid="restricoes-modal">
                <button type="button" onClick={toggleAll}
                  className={`w-full px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${allSelected ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-dashed border-slate-300 text-slate-500 hover:border-slate-400'}`}
                  data-testid="restricao-toggle-all">
                  {allSelected ? 'Exclusiva Total (desmarcar todas)' : 'Marcar Todas (Exclusiva Total)'}
                </button>
                <div className="flex flex-col gap-1.5">
                  {sameGenderFuncs.map(f => {
                    const selected = currentRestrictions.includes(f.function_id);
                    const dotColor = f.eligibility?.gender === 'feminino' ? 'bg-pink-400' : f.eligibility?.gender === 'masculino' ? 'bg-blue-400' : 'bg-violet-400';
                    return (
                      <button key={f.function_id} type="button" onClick={() => toggleRestriction(f.function_id)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors text-left ${selected ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                        data-testid={`restricao-${f.function_id}`}>
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotColor}`} />
                        <span className="flex-1">{f.nome}</span>
                        {selected && <Lock className="w-3.5 h-3.5 text-amber-600" />}
                      </button>
                    );
                  })}
                  {sameGenderFuncs.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Nenhuma outra função do mesmo gênero.</p>}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ==================== ROTATION PREVIEW DIALOG ==================== */}
      <Dialog open={rotateDialog} onOpenChange={setRotateDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RotateCcw className="w-5 h-5 text-blue-600" />Rotacionar Recitativos</DialogTitle>
            <DialogDescription className="sr-only">Configuração e sugestão de rotação de recitativos</DialogDescription>
          </DialogHeader>

          {/* Cycle config + countdown */}
          <div className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50" data-testid="cycle-config-section">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <p className="text-sm font-medium text-slate-700">Configuração do Ciclo</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <Label className="text-xs text-slate-500">Duração prevista (meses)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input type="number" min="1" max="24" value={cycleDraftMonths} onChange={(e) => setCycleDraftMonths(parseInt(e.target.value) || '')}
                    className="h-8 text-sm bg-white" placeholder="Ex: 6" data-testid="cycle-duration-input" />
                  {String(cycleDraftMonths) !== String(cycleConfig.cycle_duration_months || '') && cycleDraftMonths !== '' && (
                    <Button size="sm" onClick={() => saveCycleConfig(cycleDraftMonths)} className="h-8 bg-blue-600 hover:bg-blue-700 text-xs" data-testid="save-cycle-btn">
                      Salvar
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex-1">
                {(() => {
                  const last = cycleConfig.last_rotated_at;
                  if (!last) return (
                    <div>
                      <Label className="text-xs text-slate-500">Última rotação</Label>
                      <p className="text-sm text-slate-400 mt-1">Nunca rotacionado</p>
                    </div>
                  );
                  const lastDate = new Date(last);
                  const now = new Date();
                  const diffMs = now - lastDate;
                  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                  const diffMonths = Math.floor(diffDays / 30);
                  const remainingDays = diffDays % 30;
                  const elapsed = diffMonths > 0 ? `${diffMonths} mês${diffMonths > 1 ? 'es' : ''}${remainingDays > 0 ? ` e ${remainingDays}d` : ''}` : `${diffDays} dia${diffDays !== 1 ? 's' : ''}`;
                  const cycleDur = cycleConfig.cycle_duration_months;
                  const isOverdue = cycleDur && diffMonths >= cycleDur;
                  const pct = cycleDur ? Math.min((diffDays / (cycleDur * 30)) * 100, 100) : 0;
                  const barColor = isOverdue ? '#EF4444' : pct > 75 ? '#F59E0B' : '#3B82F6';
                  return (
                    <div>
                      <Label className="text-xs text-slate-500">Tempo desde última rotação</Label>
                      <p className={`text-sm font-semibold mt-1 ${isOverdue ? 'text-red-600' : 'text-slate-700'}`}>{elapsed}</p>
                      {cycleDur && (
                        <div className="mt-1.5">
                          <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5">{isOverdue ? 'Ciclo vencido' : `${Math.round(pct)}% do ciclo de ${cycleDur} mês${cycleDur > 1 ? 'es' : ''}`}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Gender filter */}
          <div className="flex gap-1.5" data-testid="rotate-gender-filter">
            {[{ value: 'masculino', label: 'Meninos', color: 'blue' }, { value: 'feminino', label: 'Meninas', color: 'pink' }].map(g => (
              <button key={g.value} type="button" onClick={() => setRotateGenderFilter(g.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${rotateGenderFilter === g.value ? (g.color === 'pink' ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-blue-500 bg-blue-50 text-blue-700') : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                {g.label}
              </button>
            ))}
          </div>

          {/* Suggestions table */}
          {rotateSuggestions.length > 0 && (() => {
            const filteredSuggestions = rotateSuggestions.filter(s => {
              const func = functions.find(f => f.function_id === s.function_id);
              const gender = func?.eligibility?.gender || 'todos';
              return gender === 'todos' || gender === rotateGenderFilter;
            });
            const allAssigned = new Set(filteredSuggestions.flatMap(s => s.suggested_people.map(p => p.pessoa_id)));
            const available = eligiblePeople
              .filter(p => (p.genero || '').toLowerCase() === rotateGenderFilter && p.auxiliar_jovens && !allAssigned.has(p.pessoa_id))
              .map(p => ({ pessoa_id: p.pessoa_id, nome: p.apelido || (p.nome_completo || '').split(' ')[0] }));

            const removePerson = (funcId, pessoaId) => {
              setRotateSuggestions(prev => prev.map(s => s.function_id === funcId ? { ...s, suggested_people: s.suggested_people.filter(p => p.pessoa_id !== pessoaId) } : s));
            };
            const addPerson = (funcId, person) => {
              setRotateSuggestions(prev => prev.map(s => s.function_id === funcId ? { ...s, suggested_people: [...s.suggested_people, person] } : s));
            };

            const isFem = rotateGenderFilter === 'feminino';

            return (
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className={`grid grid-cols-[1fr_2fr_2fr] px-3 py-2 ${isFem ? 'bg-pink-600' : 'bg-blue-600'}`}>
                  <p className="text-[10px] uppercase tracking-wider text-white font-semibold">Função</p>
                  <p className={`text-[10px] uppercase tracking-wider font-semibold ${isFem ? 'text-pink-100' : 'text-blue-100'}`}>Atuais</p>
                  <p className="text-[10px] uppercase tracking-wider text-white font-semibold">Sugestão</p>
                </div>
                <div className="divide-y divide-slate-200">
                  {filteredSuggestions.map(s => {
                    const func = functions.find(f => f.function_id === s.function_id);
                    const maxQty = (func?.qty_male || 0) + (func?.qty_female || 0);
                    return (
                      <div key={s.function_id} className="grid grid-cols-[1fr_2fr_2fr] px-3 py-2.5 items-center bg-white">
                        <p className="text-xs font-medium text-slate-700">{s.function_name}</p>
                        <div>
                          {s.current_people.length === 0 ? <p className="text-[11px] text-slate-300 italic">Vazio</p> : s.current_people.map(p => (
                            <p key={p.pessoa_id} className="text-[11px] text-slate-500">{p.nome}</p>
                          ))}
                        </div>
                        <div className="space-y-0.5">
                          {s.suggested_people.map(p => {
                            const isNew = !s.current_people.some(c => c.pessoa_id === p.pessoa_id);
                            return (
                              <div key={p.pessoa_id} className="flex items-center gap-1 group">
                                <span className={`text-[11px] ${isNew ? (isFem ? 'text-pink-700 font-semibold' : 'text-blue-700 font-semibold') : 'text-slate-500'}`}>{p.nome}</span>
                                <button type="button" onClick={() => removePerson(s.function_id, p.pessoa_id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-opacity"><X className="w-3 h-3" /></button>
                              </div>
                            );
                          })}
                          {s.suggested_people.length < maxQty && available.length > 0 && (
                            <select
                              className={`text-[11px] bg-transparent border-none p-0 cursor-pointer focus:ring-0 ${isFem ? 'text-pink-600' : 'text-blue-600'}`}
                              value=""
                              onChange={(e) => {
                                const p = available.find(a => a.pessoa_id === e.target.value);
                                if (p) addPerson(s.function_id, p);
                              }}
                            >
                              <option value="">+ Adicionar</option>
                              {available.map(p => <option key={p.pessoa_id} value={p.pessoa_id}>{p.nome}</option>)}
                            </select>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div className="flex items-center justify-between mt-2">
            <Button variant="ghost" size="sm" onClick={handleSuggestRotation} disabled={rotateLoading} className={`${rotateGenderFilter === 'feminino' ? 'text-pink-600 hover:bg-pink-50' : 'text-blue-600 hover:bg-blue-50'}`} data-testid="new-suggestion-btn">
              <RotateCcw className={`w-3.5 h-3.5 mr-1 ${rotateLoading ? 'animate-spin' : ''}`} />{rotateSuggestions.length > 0 ? 'Nova sugestão' : 'Gerar sugestão'}
            </Button>
            {rotateSuggestions.length > 0 && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setRotateDialog(false)}>Cancelar</Button>
                <Button size="sm" onClick={handleApplyClick} disabled={rotateLoading} className={`${rotateGenderFilter === 'feminino' ? 'bg-pink-600 hover:bg-pink-700' : 'bg-blue-600 hover:bg-blue-700'}`} data-testid="apply-rotation-btn">
                  {rotateLoading ? 'Aplicando...' : 'Aplicar'}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Early apply confirmation */}
      <Dialog open={confirmEarlyApply} onOpenChange={setConfirmEarlyApply}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-amber-600 flex items-center gap-2">Ciclo ainda em andamento</DialogTitle>
            <DialogDescription className="sr-only">Confirmação de rotação antecipada</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            O ciclo de <strong>{cycleConfig.cycle_duration_months} mês{(cycleConfig.cycle_duration_months || 0) > 1 ? 'es' : ''}</strong> ainda não foi concluído.
            {(() => {
              const last = cycleConfig.last_rotated_at;
              if (!last) return '';
              const diffDays = Math.floor((new Date() - new Date(last)) / (1000 * 60 * 60 * 24));
              const diffMonths = Math.floor(diffDays / 30);
              const rest = diffDays % 30;
              const elapsed = diffMonths > 0 ? `${diffMonths} mês${diffMonths > 1 ? 'es' : ''}${rest > 0 ? ` e ${rest} dias` : ''}` : `${diffDays} dia${diffDays !== 1 ? 's' : ''}`;
              const remaining = (cycleConfig.cycle_duration_months * 30) - diffDays;
              return ` Passaram-se ${elapsed}. Faltam aproximadamente ${remaining} dias.`;
            })()}
          </p>
          <p className="text-sm text-slate-500">Deseja aplicar a rotação mesmo assim?</p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmEarlyApply(false)}>Cancelar</Button>
            <Button size="sm" onClick={handleApplyRotation} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-early-apply-btn">Sim, aplicar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FuncTooltipContent({ func }) {
  if (!func) return <p className="text-xs">Função não encontrada</p>;
  const elig = func.eligibility || {};
  const genderLabel = elig.gender === 'masculino' ? 'Só meninos' : elig.gender === 'feminino' ? 'Só meninas' : 'Todos';
  const filtros = (elig.filtros_pessoa || []).map(k => BOOLEAN_FIELDS.find(b => b.key === k)?.label || k);
  const hasAge = (elig.idade_min > 0 || (elig.idade_max && elig.idade_max < 99));
  return (
    <div className="text-xs space-y-1">
      <p className="font-semibold">{func.nome}</p>
      <div className="space-y-0.5 text-slate-300">
        <p>Gênero: {genderLabel}</p>
        {(func.qty_male > 0 || func.qty_female > 0) && <p>Vagas: {func.qty_male > 0 ? `${func.qty_male} menino${func.qty_male > 1 ? 's' : ''}` : ''}{func.qty_male > 0 && func.qty_female > 0 ? ', ' : ''}{func.qty_female > 0 ? `${func.qty_female} menina${func.qty_female > 1 ? 's' : ''}` : ''}</p>}
        {hasAge && <p>Idade: {elig.idade_min || 0} - {elig.idade_max || 99} anos</p>}
        {filtros.length > 0 && <p>Filtros: {filtros.join(', ')}</p>}
      </div>
    </div>
  );
}

function RotationSideSelector({ label, side, selected, availableFunctions, onToggle, colorClass }) {
  const bgClass = colorClass === 'cyan' ? 'border-cyan-500 bg-cyan-50 text-cyan-700' : 'border-teal-500 bg-teal-50 text-teal-700';
  const inactiveClass = 'border-slate-200 text-slate-500 hover:border-slate-300';
  const genderDotClass = (f) => {
    const g = f.eligibility?.gender;
    if (g === 'masculino') return 'bg-blue-400';
    if (g === 'feminino') return 'bg-pink-400';
    return 'bg-violet-400';
  };
  return (
    <div>
      <Label className="text-xs mb-1.5 block">{label}</Label>
      <div className="flex flex-wrap gap-1.5" data-testid={`rot-${side}`}>
        {availableFunctions.length === 0 && <p className="text-xs text-slate-400">Nenhuma função disponível</p>}
        {availableFunctions.map(f => {
          const isSelected = selected.includes(f.function_id);
          return (
            <TooltipProvider key={f.function_id}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" onClick={() => onToggle(f.function_id)}
                    className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border transition-colors ${isSelected ? bgClass : inactiveClass}`}
                    data-testid={`rot-${side}-${f.function_id}`}>
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${genderDotClass(f)}`} />
                    {f.nome}
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <FuncTooltipContent func={f} />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    </div>
  );
}
